import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Collapse, Table, TableBody, TableCell, TableRow } from '@mui/material';
import classnames from 'classnames';
import moment from 'moment';

import AdminSupplementStatusCount from '@components/AdminSupplementStatusCount/AdminSupplementStatusCount';
import UpDownButton from '@components/UpDownButton/UpDownButton';
import useToggle from '@hooks/useToggle';
import { TAdminSupplementInquiryStatus } from '@interfaces/adminSupplements';
import { TInquiryStatus } from '@interfaces/inquiries';
import { adminSupplementDetailsSelector } from '@store/features/adminInquiries/supplementDetails/selectors';
import useOrganizationById from '@store/hooks/useOrganizationById';
import formatDate from '@utils/formatDate';
import enumKeys from '@utils/getEnumValues';
import getShortenText from '@utils/getShortenText';

import SendInquiry from '../SendInquiry/SendInquiry';
import TableGridDetailsRow from '../TableGridDetailsRow/TableGridDetailsRow';

import { ITableGridRowProps } from './TableGridRow.interface';

import './style.scss';

const TableGridRow: FC<ITableGridRowProps> = ({ className, row }) => {
  const { t } = useTranslation();

  const organization = useOrganizationById(row.organizationId);
  const adminSupplementInquiryDetails = useSelector(adminSupplementDetailsSelector);
  const isOpen = useMemo(
    () =>
      row.adminSupplementInquiryDetailsDto.some(
        value => value.supplementInquiryId === adminSupplementInquiryDetails?.supplementInquiryId,
      ),
    [adminSupplementInquiryDetails?.supplementInquiryId, row.adminSupplementInquiryDetailsDto],
  );

  const [open, , , handleToggle] = useToggle(isOpen);

  const isPending = row.inquiryStatusId === TInquiryStatus.Pending;
  const isNeedAdminResponse =
    row.adminSupplementInquiryDetailsDto.every(
      supplementInquiry => !supplementInquiry.needsAdminResponse,
    ) && row.inquiryStatusId !== TInquiryStatus.Complete;
  const getAdminSupplementInquiryStatusesCount = useCallback(
    (status: number): number =>
      row.adminSupplementInquiryDetailsDto.reduce((accumulator, object) => {
        if (object.adminSupplementInquiryStatusId === status) {
          return accumulator + 1;
        }
        return accumulator;
      }, 0),
    [row.adminSupplementInquiryDetailsDto],
  );
  const TAdminSupplementInquiryStatusIds = enumKeys(TAdminSupplementInquiryStatus, true);

  return (
    <>
      <TableRow
        className={classnames('admin-inquiries-table', className, {
          'admin-inquiries-table_open': open,
        })}
      >
        <TableCell>{formatDate(row.submittedOn)}</TableCell>
        <TableCell className="admin-inquiries-table__id-cell">{row.id}</TableCell>
        <TableCell>
          <Box className="admin-inquiries-table__chimps">
            {TAdminSupplementInquiryStatusIds.map(
              (idString: string) =>
                getAdminSupplementInquiryStatusesCount(+idString) > 0 && (
                  <AdminSupplementStatusCount
                    key={idString}
                    value={+idString}
                    count={getAdminSupplementInquiryStatusesCount(+idString)}
                    hideLabel
                  />
                ),
            )}
          </Box>
        </TableCell>
        <TableCell title={organization?.name}>{getShortenText(organization?.name)}</TableCell>
        <TableCell>{t('adminInquiries.supplement')}</TableCell>
        <TableCell>{moment.duration(row.timeElapsed).humanize()}</TableCell>
        <TableCell>
          {isPending ? (
            <HelpOutlineIcon color="success" />
          ) : (
            <Box component="img" src="/images/img-submit-green.svg" />
          )}
        </TableCell>
        <TableCell>
          <UpDownButton onOpen={handleToggle} open={open} />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell className="admin-inquiries-table__collapse" colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Table size="small" aria-label="inquiry details">
              <TableBody>
                {row.adminSupplementInquiryDetailsDto.map(supplementInquiry => (
                  <TableGridDetailsRow
                    key={`${supplementInquiry.supplementInquiryId}}`}
                    supplementInquiry={supplementInquiry}
                  />
                ))}
                {isNeedAdminResponse && <SendInquiry row={row} />}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default TableGridRow;
